/* eslint-disable react-hooks/exhaustive-deps */
import {PropsWithChildren, ReactElement, useEffect, useState} from "react";
import {
    hasEnvironmentChanged,
    hasRuntimeChanged,
    RuntimeActivator,
    RuntimeEvent
} from "@common-core/runtime-js/runtime";
import {toUrl} from "@common-core/runtime-js/support";
import {RuntimeContext, RuntimeContextValue} from "./RuntimeContext";

export interface RuntimeProviderProps<M, E> {
  activator: RuntimeActivator<M, E>;
}

export function RuntimeProvider<M, E>({
  activator,
  children
}: PropsWithChildren<RuntimeProviderProps<M, E>>): ReactElement<any, any> {
  if (!activator.activated()) {
    activator.activate(window.location);
  }

  const [value, setValue] = useState<RuntimeContextValue<M, E>>(() => ({
    activator,
    version: 0
  }));

  useEffect(() => {
    const switchRuntime = (event: RuntimeEvent<M, E>) => {
        // If the selected runtime has changed, set the browser location to the new runtime
        if (hasRuntimeChanged(event)) {
            window.location.assign(toUrl(event.current.runtime));
        }
        // Otherwise, if only the environment has changed; trigger a state change to rerender
        else if (hasEnvironmentChanged(event)) {
            setValue(previous => ({activator, version: previous.version + 1}));
        }
    };
    activator.onActivation(switchRuntime);
    return () => {
      activator.remove(switchRuntime);
    };
  }, []);

  return (
    <RuntimeContext.Provider value={value}>
      {children}
    </RuntimeContext.Provider>
  );
}
